import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProductComparisonCard from "~/features/compareProducts/components/ProductComparisonCard";
import { useOnMount } from "~/common/hooks/useOnMount";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import EmptyComparisonPage from "~/features/compareProducts/components/EmptyComparison";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { useRegularPnaProductDescriptors } from "~/domains/pna/useRegularPnaProductDescriptors";
import { type RootState } from "~/app/store";
import {
  useAddProductsToComparisonMutation,
  useProductComparisonQuery,
  useRemoveAllProductsFromComparisonMutation,
} from "~/features/compareProducts/api/productComparisonQuery";
import {
  trackAddToComparisonClick,
  trackClearComparisonClick,
  trackPCDownload,
  trackProductComparisonDialogOpen,
} from "~/features/compareProducts/utils/analytics";
import { ReduxImperialToggle } from "~/domains/userPreferences/components/UnitModeToggle";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { downloadPC } from "~/features/compareProducts/api/DownloadFile";
import { QuickEntryPopup } from "~/features/quickEntry/v2/QuickEntryPopup";
import { Trans, useTranslation } from "react-i18next";
import { hasProductConfiguredPC } from "~/features/quickEntry/utils/helpers";
import { addMessage } from "~/features/toasts/redux/actions";
import { SIZE_DESKTOP_S, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Card, Drawer, IconButton, Stack, styled, Switch, Typography } from "~/common/mui";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DrawerContent } from "~/common/components/Drawer/Drawer";
import { DrawerHeader } from "~/features/cart/B2CCart/components/GuestCheckoutDialog/components/GuestCheckoutCommon";
import { ComparisonScrollController } from "~/features/compareProducts/components/ComparisonScrollController";
import { userIsRegistered } from "~/domains/user/utils/roles";
import ProductInformationTable from "~/features/compareProducts/components/ProductInformationTable";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { C_ON_SURFACE } from "~/common/components/icon/Colors";
import ProductDetailsTable from "~/features/compareProducts/components/ProductDetailsTable";
import CommercialTable from "~/features/compareProducts/components/ComercialTable";
import CharacteristicsTable from "~/features/compareProducts/components/CharacteristicsTable";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { ComparisonOverlayContent } from "./ComparisonOverlayStyles";

const CARD_COLLAPSE_SCROLL_THRESHOLD = 16;
const CARD_EXPAND_SCROLL_THRESHOLD = 4;

interface Props {
  data: PdpPageProductData[];
  maxSize: number;
  showSidebar: boolean;
  hideDrawer: () => void;
  showOnlyDifference: boolean;
  toggleOnlyDifference: () => Promise<void>;
  pinnedProductCodes: string[];
  onPinClick: (productCode: string) => void;
  onUnPinClick: (productCode: string) => void;
}

export interface ProductComparisonContentRef {
  clearAll: () => Promise<void>;
}

/**
 * Comparison membership descriptors through the canonical PnA queries. Mirrors the legacy
 * `selectProductDescriptor` mapping: one price query per selected price reference, one
 * availability query per product. Canonical options disable configurator codes because
 * comparison never fetches configurator PnA.
 */
function useComparisonProductDescriptors(
  productCodes: string[],
  priceReference: string,
): ProductDescriptor<PdpPageProductData>[] {
  const basicProducts = useSelector((state: RootState) => state.products.basic);
  return useRegularPnaProductDescriptors(
    productCodes.map((productCode) => ({
      storefrontCode: productCode,
      base: basicProducts[productCode],
      priceReference,
    })),
  ) as ProductDescriptor<PdpPageProductData>[];
}

export const ProductComparisonContent = forwardRef<ProductComparisonContentRef, Props>(
  (
    {
      data,
      maxSize,
      showSidebar,
      hideDrawer,
      showOnlyDifference,
      toggleOnlyDifference,
      pinnedProductCodes,
      onPinClick,
      onUnPinClick,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { selection, status } = usePriceReferenceSelection();
    const size = useNumericScreenSize();
    const isMobile = size < useNumericScreenSize.tablet;
    const isTablet = size <= useNumericScreenSize.tablet;
    const isDesktop = size >= SIZE_DESKTOP_S;

    const [renderChildren, setRenderChildren] = useState(false);
    const [scrollAtCards, setScrollAtCards] = useState(false);
    const { val: statusModal, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);
    const { data: comparisonData } = useProductComparisonQuery();
    const productCodes = useMemo(() => comparisonData?.productCodes ?? [], [comparisonData?.productCodes]);
    const addProductsMutation = useAddProductsToComparisonMutation();
    const clearProductsMutation = useRemoveAllProductsFromComparisonMutation();
    const unitMode = useUnitMode();
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const cardsContainerRef = useRef<HTMLDivElement>(null);
    const tablesContainerRef = useRef<HTMLDivElement>(null);

    const handleCardsScroll = () => {
      if (cardsContainerRef.current && tablesContainerRef.current) {
        tablesContainerRef.current.scrollLeft = cardsContainerRef.current.scrollLeft;
      }
    };

    const handleTablesScroll = () => {
      if (cardsContainerRef.current && tablesContainerRef.current) {
        cardsContainerRef.current.scrollLeft = tablesContainerRef.current.scrollLeft;
      }
    };

    useImperativeHandle(ref, () => ({
      clearAll: handleRemoveAllClick,
    }));

    const productDescriptorList = useComparisonProductDescriptors(productCodes, getInitialPriceReference(selection));

    const pageContainsSurcharge =
      productDescriptorList.length > 0 &&
      productDescriptorList.some(
        (product) => product.prices.length === 1 && (product.prices[0].surchargeScale[0]?.surcharge ?? 0) > 0,
      );

    const pageContainsTariffSurcharge =
      productDescriptorList.length > 0 &&
      productDescriptorList.some(
        (product) => product.prices.length === 1 && product.prices[0].isTariffSurchargeApplicable,
      );

    useEffect(() => {
      const element = dialogContentRef.current;
      if (!element) {
        return;
      }
      const handleScroll = () => {
        const { scrollTop } = element;
        setScrollAtCards((isCollapsed) =>
          isCollapsed ? scrollTop > CARD_EXPAND_SCROLL_THRESHOLD : scrollTop >= CARD_COLLAPSE_SCROLL_THRESHOLD,
        );
      };
      element.addEventListener("scroll", handleScroll);
      return () => {
        element.removeEventListener("scroll", handleScroll);
      };
    }, []);

    const productDesignations = productDescriptorList.map((descriptor) => descriptor.base.designation);

    const dispatch = useDispatch();
    const handleRemoveAllClick = async () => {
      trackClearComparisonClick(productDesignations);
      await clearProductsMutation.mutateAsync();
    };

    useOnMount(() => trackProductComparisonDialogOpen(productDesignations));

    useOnMount(() => {
      if (data.length > 0) {
        dispatch(addBaseProductDataAction(data));
      }
      setRenderChildren(status === "ready");
    });

    const placeholders = productCodes.length < maxSize ? [productCodes.length] : []; //  Array.from({ length: maxSize - productCodes.length }, (_, idx) => productCodes.length + idx);

    const downloadFunc = (type: "PDF" | "XLSX") => {
      trackPCDownload(type, productDesignations);
      downloadPC(type, unitMode);
    };

    const handlePin = (productCode: string) => {
      if (pinnedProductCodes.includes(productCode)) {
        onUnPinClick(productCode);
      } else {
        onPinClick(productCode);
      }
    };

    const sortedCodes = useMemo(() => {
      const availablePinnedCodes = pinnedProductCodes.filter((code) => productCodes.includes(code));
      return [...availablePinnedCodes, ...productCodes.filter((code) => !availablePinnedCodes.includes(code))];
    }, [productCodes, pinnedProductCodes]);

    const sortedProductDescriptors = useMemo(
      () =>
        sortedCodes
          .map((code) => productDescriptorList.find((product) => product.base.code === code))
          .filter((product): product is ProductDescriptor<PdpPageProductData> => product !== undefined),
      [productDescriptorList, sortedCodes],
    );

    const hasCharacteristics = productDescriptorList.some(
      (product) => (product.base.specificationBand?.length ?? 0) > 0,
    );
    const hasProductInformation = productDescriptorList.some(
      (product) => product.base?.technicalDetails?.some((section) => section.entries.length > 0),
    );

    const quickEntryPopup = (
      <QuickEntryPopup
        onClose={closeModal}
        maxAmount={maxSize - productCodes.length}
        validateRow={(row, itemsPopup = []) => {
          const errors: ReactNode[] = [];
          const filteredItemsPopup = itemsPopup
            .filter((item) => item.id !== row.id)
            .map(function (item) {
              return item.productId;
            });
          const dataPagePlusPopup = [...productDesignations, ...filteredItemsPopup];
          if (dataPagePlusPopup.includes(row.product.designation)) {
            errors.push(
              <Typography pl={1.5} color="error" variant="bodySmall">
                <Trans i18nKey={"bulkUpload.productComparison.error.repeatedProduct"}>
                  The product <span>{{ designation: row.product.designation }}</span> is already in comparison list.
                  Choose different product to compare.
                </Trans>
              </Typography>,
            );
          }
          return errors;
        }}
        onAdd={async (form, setIsSending, onClose) => {
          const items = form.items.filter(hasProductConfiguredPC);
          const arrProd = items.map((item) => item.product.code);
          setIsSending(true);
          try {
            const result = await addProductsMutation.mutateAsync(arrProd);
            if (result.kind === "bulk-added") {
              const productData = result.products.map((product) => transformProduct(product));
              if (productData.length > 0) {
                dispatch(addBaseProductDataAction(productData, true));
              }
            }
            const designations = items.map((item) => item.product.designation);
            trackAddToComparisonClick(designations, true, "Product Comparison");
            if (arrProd.length > 0) {
              dispatch(addMessage("info", t("addToPC.success", { count: arrProd.length })));
            }
            onClose();
          } catch (error) {
            console.error("Failed to add products to comparison", error);
            dispatch(addMessage("error", t("comparisonSnack.failedToAdd")));
          } finally {
            setIsSending(false);
          }
        }}
        addButtonLabel={t("comparisonPage.modal.add.submitBtn")}
      />
    );

    const onlyDifferenceToggle = (
      <Stack direction={"row"} gap={1} alignItems={"center"}>
        <Switch checked={showOnlyDifference} onChange={toggleOnlyDifference} />
        <Typography typography="bodyLarge">{t("comparision.overlay.setting.onlyDiff")}</Typography>
      </Stack>
    );

    const displaySettings = (
      <ComparisonSidebarContent sx={{ minWidth: isMobile ? "100%" : isTablet ? "396px" : "222px" }}>
        <StyledReduxImperialToggle page={"Comparison List"} fullWidth />
        {!isTablet && onlyDifferenceToggle}
        <Stack gap={1}>
          {isTablet && (
            <Typography variant="titleMedium" component="b">
              {t("comparision.overlay.setting.downloadTitle")}
            </Typography>
          )}

          <Stack direction={isTablet ? "column" : "row"} gap={isTablet ? 0 : 1} alignItems={"flex-start"}>
            <Button startIcon={<FileDownloadOutlinedIcon />} variant="text" onClick={() => downloadFunc("PDF")}>
              {t("downloadFile.type.pdf")}
            </Button>
            <Button startIcon={<FileDownloadOutlinedIcon />} variant="text" onClick={() => downloadFunc("XLSX")}>
              {t("downloadFile.type.excel")}
            </Button>
          </Stack>
        </Stack>
      </ComparisonSidebarContent>
    );
    return (
      <ComparisonOverlayContent
        ref={dialogContentRef}
        sx={{
          px: isMobile ? 2 : 3,
          overflowAnchor: "none",
        }}
      >
        {productCodes.length > 0 ? (
          <Stack gap={4}>
            <StickyContainer sx={{ pb: scrollAtCards ? 3 : 0 }}>
              {isTablet && onlyDifferenceToggle}
              <Stack gap={3} direction="row">
                {!isTablet && displaySettings}
                <Stack gap={2} sx={{ minWidth: 0 }}>
                  {!isMobile && <ComparisonScrollController scrollContainerRef={cardsContainerRef} />}

                  <Stack direction="row" gap={1} sx={{ overflow: "hidden" }}>
                    <ScrollableCardsList ref={cardsContainerRef} onScroll={handleCardsScroll}>
                      {renderChildren &&
                        sortedCodes.map((code) => (
                          <ProductComparisonCard
                            key={code}
                            productCode={code}
                            smallCardView={scrollAtCards}
                            handlePin={handlePin}
                            isPinned={pinnedProductCodes.includes(code)}
                          />
                        ))}
                      {productCodes.length < maxSize && (
                        <AddToCompareCard onClick={openModal}>
                          <IconButton variant="contained" color={"secondary"}>
                            <AddIcon sx={{ fontSize: isDesktop ? 24 : 20 }} />
                          </IconButton>
                          <Typography variant="bodyLarge">{t("comparisonPage.addProduct.title")}</Typography>
                        </AddToCompareCard>
                      )}
                    </ScrollableCardsList>
                  </Stack>
                </Stack>
              </Stack>
            </StickyContainer>
            <Stack gap={2}>
              <SharedTablesScroll ref={tablesContainerRef} onScroll={handleTablesScroll}>
                <SharedTablesContent>
                  <ProductDetailsTable
                    products={sortedProductDescriptors}
                    placeholders={placeholders}
                    showOnlyDifference={showOnlyDifference}
                  />
                  {!userIsRegistered() && (
                    <CommercialTable
                      products={sortedProductDescriptors}
                      placeholders={placeholders}
                      showOnlyDifference={showOnlyDifference}
                    />
                  )}

                  {hasProductInformation && (
                    <ProductInformationTable
                      products={sortedProductDescriptors}
                      placeholders={placeholders}
                      showOnlyDifference={showOnlyDifference}
                    />
                  )}
                  {hasCharacteristics && (
                    <CharacteristicsTable
                      products={sortedProductDescriptors}
                      placeholders={placeholders}
                      showOnlyDifference={showOnlyDifference}
                    />
                  )}
                </SharedTablesContent>
              </SharedTablesScroll>
              <Disclaimers className="scha__comparison-page__disclaimers">
                <Alert severity={"info"}>{t("comparisonPage.selectUnitType")}</Alert>
                {
                  <IncludesSurchargeDisclaimer
                    hasSurcharge={pageContainsSurcharge}
                    tariffSurcharge={pageContainsTariffSurcharge}
                  />
                }
              </Disclaimers>
            </Stack>
          </Stack>
        ) : (
          <EmptyComparisonPage openQuickEntry={openModal} />
        )}
        {statusModal && quickEntryPopup}
        <Drawer anchor={isMobile ? "bottom" : "left"} open={showSidebar} onClose={hideDrawer} sx={{ zIndex: 10000 }}>
          <DrawerHeader onClose={hideDrawer}>
            <Typography variant="titleLarge" component="b">
              {t("comparision.overlay.setttingDrawer.title")}
            </Typography>
          </DrawerHeader>
          <DrawerContent>{displaySettings}</DrawerContent>
        </Drawer>
      </ComparisonOverlayContent>
    );
  },
);

const StickyContainer = styled(Stack)(({ theme }) => ({
  gap: "16px",
  position: "sticky",
  top: 0,
  zIndex: 10,
  paddingTop: theme.spacing(2),
  backgroundColor: "white",
}));

const ComparisonSidebarContent = styled(Stack)(({ theme }) => ({
  position: "sticky",
  left: 0,
  zIndex: 9999,
  backgroundColor: theme.palette.common.white,
  gap: "16px",
}));

const ScrollableCardsList = styled("div")(({ theme: _theme }) => ({
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const SharedTablesScroll = styled(Box)(({ theme }) => ({
  overflowX: "auto",
  overflowY: "hidden",
  // Keep the shared scroller as the sticky labels' nearest scroll container.
  "&& .TanStackTable-tableContainer": {
    overflow: "visible",
  },
  [theme.breakpoints.up("desktop-s")]: {
    "& .TanStackTable-pinnedLeft:first-child": {
      position: "sticky",
      left: 0,
      zIndex: 3,
    },
    "& .MuiAccordionSummary-content > .MuiTypography-root": {
      position: "sticky",
      left: theme.spacing(2),
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      position: "sticky",
      right: theme.spacing(2),
    },
  },
}));

const SharedTablesContent = styled(Box)({
  width: "max-content",
  minWidth: "100%",
  "& > .MuiAccordion-root:first-of-type::before": {
    top: "1px",
  },
});

const AddToCompareCard = styled(Card)(({ theme }) => ({
  width: "242px",
  minWidth: "242px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  border: `1px dashed ${theme.palette.divider}`,
  cursor: "pointer",
  transition: "background-color 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.surfaceContainer.high,
  },
}));

const Disclaimers = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3.75),
}));

const StyledReduxImperialToggle = styled(ReduxImperialToggle)(({ theme }) => ({
  "& .MuiInputLabel-root": {
    fontWeight: "bold",
    ...getFontStyle(FontSize.TitleMedium),
    color: `${C_ON_SURFACE} !important`,
    marginBottom: "8px",
    [theme.breakpoints.up("desktop-s")]: {
      display: "none",
    },
  },
  "& .MuiInputLabel-shrink": {
    transform: "none",
  },
}));
